import { firestoreFieldValueArrayUnion, firestoreFieldValueDelete, isAdminFirestoreAdapter, allFromSnapshot, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { guid, silentLogger } from "@buildwithflux/shared";
/**
 * The Firestore implementation of the OrganizationPrivateMetadataRepository
 *
 * Note that Firestore rules dictate that:
 *  - From the client adapter, this collection is read-only, and can only be read by organization owners
 */
export class FirestoreOrganizationPrivateMetadataRepository {
    db;
    logger;
    constructor(db, logger = silentLogger) {
        this.db = db;
        this.logger = logger;
    }
    /** @inheritDoc */
    async getByUid(organizationUid) {
        return oneFromSnapshot(await this.db.organizationPrivateMetadata(organizationUid).get(), () => undefined);
    }
    /** @inheritDoc */
    async update(organizationUid, updateWith) {
        await this.db.organizationPrivateMetadata(organizationUid).set({
            uid: organizationUid,
            ...updateWith,
        }, { merge: true });
    }
    /** @inheritDoc */
    async updateSubscription(organizationUid, customerUid, keyToUpdate, updatedSubscriptionInformation) {
        await this.db.organizationPrivateMetadata(organizationUid).update({
            uid: organizationUid,
            [`payment.${keyToUpdate}.${updatedSubscriptionInformation.uid}`]: updatedSubscriptionInformation,
            "payment.customerUid": customerUid,
        });
    }
    /**  @inheritDoc */
    async *getAllForCount() {
        if (!isAdminFirestoreAdapter(this.db)) {
            throw new Error("Only admin Firestore adapters can get organizations for count");
        }
        const pageSize = 100;
        const query = this.db
            .organizationPrivateMetadataCollection()
            .where("editors.needsCountAfter", "!=", null)
            .orderBy("editors.needsCountAfter", "asc")
            .limit(pageSize);
        let lastOrganizationPrivateMetadata = null;
        while (true) {
            const snapshot = lastOrganizationPrivateMetadata
                ? await query.startAfter(lastOrganizationPrivateMetadata).get()
                : await query.get();
            if (snapshot.empty) {
                break;
            }
            yield* allFromSnapshot(snapshot);
            lastOrganizationPrivateMetadata = snapshot.docs[snapshot.docs.length - 1] ?? null;
        }
    }
    /** @inheritDoc */
    async resetNeedsCountAfter(organizationUid) {
        await this.db.organizationPrivateMetadata(organizationUid).update({
            "editors.needsCountAfter": await firestoreFieldValueDelete(this.db),
        });
    }
    /** @inheritdoc */
    async createApiKeyId(organizationUid) {
        // Pre-set in case the OPM doesn't exist yet
        await this.db.organizationPrivateMetadata(organizationUid).set({ uid: organizationUid }, { merge: true });
        const id = guid();
        await this.db.organizationPrivateMetadata(organizationUid).update({
            "api.keys": (await firestoreFieldValueArrayUnion(this.db))({ id, createdAt: Date.now() }),
        });
        return id;
    }
    /** @inheritdoc */
    async getAllFromMembersByOrganizationUid(organizationMembers) {
        if (organizationMembers.length === 0) {
            // Prevents 'Invalid Query. A non-empty array is required for 'in' filters'
            return {};
        }
        const getOneSafelyByUid = async (uid) => {
            try {
                const snapshot = await this.db.organizationPrivateMetadata(uid).get();
                return oneFromSnapshot(snapshot, () => undefined);
            }
            catch (error) {
                this.logger.warn(`Could not retrieve details for organization ${uid}`, error);
                return undefined;
            }
        };
        const promises = organizationMembers.map((it) => it.organizationUid).map(getOneSafelyByUid);
        const metadataRecords = await Promise.all(promises);
        const metadataByUid = {};
        for (const metadata of metadataRecords) {
            if (metadata) {
                metadataByUid[metadata.uid] = metadata;
            }
        }
        return metadataByUid;
    }
}
//# sourceMappingURL=FirestoreOrganizationPrivateMetadataRepository.js.map